"use client";

import { ImageResponseData } from "@/restapi/image";
import { FC, useMemo, useCallback } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { FilterImageCategory } from "../hooks/useGalleryCategory";
import { getRandomNumberInRange } from "@/lib/utils";
import ImageShuffledCard from "@/components/ui/card/ImageShuffledCard";
import clsx from "clsx";
import { User } from "next-auth";

const predefinedLayouts = [
  ["row-span-3 col-span-1", "row-span-2 col-span-1", "row-span-2 col-span-1", "row-span-1 col-span-1", "col-span-2 row-span-2"],
  ["col-span-2 row-span-2", "row-span-2 col-span-1", "row-span-1 col-span-1", "row-span-2 col-span-1", "row-span-1 col-span-1"],
  ["row-span-1 col-span-1", "row-span-2 col-span-1", "row-span-2 col-span-1", "row-span-2 col-span-1", "col-span-1 row-span-1"],
  ["row-span-2 col-span-1", "row-span-1 col-span-1", "row-span-2 col-span-1", "row-span-1 col-span-1", "col-span-2 row-span-2"],
  ["row-span-2 col-span-1", "row-span-1 col-span-1", "row-span-3 col-span-1", "row-span-2 col-span-1", "col-span-2 row-span-2"],
];

type Props = {
  loadMore: () => void;
  hasMore: boolean;
  isLoading: boolean;
  items: ImageResponseData[];
  category: FilterImageCategory;
  handleChangeScReenType: (imageId: string) => void;
  className?: string;
  user?: User;
};

const RandomizedMasonryGrid: FC<Props> = ({
  loadMore,
  handleChangeScReenType,
  hasMore,
  category,
  isLoading,
  items,
  className,
  user,
}) => {
  /** 5件単位でレイアウトを固定生成 */
  const layoutIndexes = useMemo(() => {
    const groups = Math.ceil(items.length / 5);
    return Array.from({ length: groups }, () =>
      getRandomNumberInRange({ min: 0, max: predefinedLayouts.length - 1 })
    );
  }, [items.length]);

  const getClassName = useCallback(
    (index: number) => {
      const groupIndex = Math.floor(index / 5);
      const layout = predefinedLayouts[layoutIndexes[groupIndex] ?? 0];
      return layout[index % layout.length];
    },
    [layoutIndexes]
  );

  const handleClick = useCallback(
    (imageId: string) => {
      handleChangeScReenType(imageId);
    },
    [handleChangeScReenType]
  );

  return (
    <div className={clsx("max-w-5xl flex justify-center p-1 pt-4 mb-10", className)}>
      <InfiniteScroll
        key={`masonry-${category}`}
        dataLength={items.length}
        next={loadMore}
        hasMore={hasMore}
        loader={<div className="py-6" />}
      >
        <div className="grid grid-cols-2 gap-[2px] max-w-3xl auto-rows-fr">
          {items.map((image, index) => (
            <ImageShuffledCard
              key={image.uuid}
              className={clsx(getClassName(index), "h-full w-full")}
              params={{
                id: image.uuid,
                src: image.imageFile.summaryFileUrl ?? image.imageFile.fileUrl,
                averageScore: image.averageScore,
                isLocked: image.isLocked,
              }}
              onClick={handleClick}
            />
          ))}
        </div>
      </InfiniteScroll>
    </div>
  );
};

export default RandomizedMasonryGrid;